import { useFrame, useLoader, useThree } from "@react-three/fiber";
import { useGLTF, Float, Html, GradientTexture, OrbitControls } from "@react-three/drei";
import { useRef, useEffect, useState } from "react";
import { useControls } from "leva";
import * as THREE from "three";
import { TextureLoader } from "three/src/loaders/TextureLoader";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";

// Animation function
const vertex = new THREE.Vector3();
const origin = new THREE.Vector3(0, 0, 0); // Define the origin for distance calculations

const animateMeshVertices = (geometry, originalYCoords) => {
  for (let i = 0; i < geometry.attributes.position.count; i++) {
    vertex.fromBufferAttribute(geometry.attributes.position, i);

    // Use the stored original Y coordinate as base
    const originalY = originalYCoords[i];
    vertex.y = originalY + Math.sin(vertex.distanceTo(origin) + performance.now() * 0.002) * 0.185;

    geometry.attributes.position.setXYZ(i, vertex.x, vertex.y, vertex.z);
  }
  geometry.attributes.position.needsUpdate = true;
};

export default function Experience({ toggleAnimation, elementsVisible, cameraAnimationsEnabled }) {
  const aalto = useGLTF("/wave-final-three.glb");
  const normalTexture = useLoader(TextureLoader, "water-normal-05.jpg");

  const [hdrEquirect, setHdrEquirect] = useState(null);

  const meshRef = useRef();
  const { camera, mouse } = useThree();

  let originalYCoords = useRef([]);

  const handleDiveInClick = () => {
    toggleAnimation(); // Call the function to toggle the animation
  };

  useFrame(() => {
    // Animate mesh vertices
    animateMeshVertices(meshRef.current.geometry, originalYCoords.current);

    if (cameraAnimationsEnabled) {
      const damping = 0.05;
      const targetX = mouse.x * Math.PI * 1.5;
      const targetY = mouse.y * Math.PI * 2.25;
      camera.position.x += (targetX - camera.position.x) * damping;
      camera.position.y += (targetY - camera.position.y) * damping;
      camera.lookAt(0, 0, 0);
    } else {
      camera.lookAt(0, 0, 0);
    }
  });

  useEffect(() => {
    if (meshRef.current?.geometry?.attributes?.position && originalYCoords.current.length === 0) {
      originalYCoords.current = Array.from(meshRef.current.geometry.attributes.position.array).filter((_, index) => index % 3 === 1);
    }
  }, [meshRef.current?.geometry?.attributes?.position]);

  useEffect(() => {
    // Existing code for text animations
    setTimeout(() => {
      document.querySelector(".ready").classList.add("animate-ready");
    }, 100);

    setTimeout(() => {
      document.querySelector(".hr-line.first-line").classList.add("animate-first-line");
    }, 250);

    setTimeout(() => {
      document.querySelector(".for-an").classList.add("animate-for-an");
    }, 750);

    setTimeout(() => {
      document.querySelector(".hr-line.second-line").classList.add("animate-second-line");
    }, 850);

    setTimeout(() => {
      document.querySelector(".exit").classList.add("animate-exit");
    }, 1200);

    setTimeout(() => {
      document.querySelector(".hr-line--short.third-line").classList.add("animate-third-line");
    }, 1250);

    setTimeout(() => {
      document.querySelector(".dive-in-button").classList.add("animate-dive-in");
    }, 1500);
  }, []);

  // useEffect for HDR texture
  useEffect(() => {
    new RGBELoader().load("environmentMaps/night-2-HDR.hdr", (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      setHdrEquirect(texture);
    });
  }, []);

  const ambientLightControls = useControls("AmbientLight", {
    ambientLightIntensity: {
      value: 6.5,
      min: 0,
      max: 10,
      step: 0.01,
    },
    ambientLightColor: {
      value: "#061526",
    },
  });

  const aaltoOneControls = useControls("Aalto", {
    aaltoOnePosition: { value: { x: 3.5, y: -10, z: 0 }, step: 0.1 },
    aaltoOneRotation: { value: { x: 0, y: 4.6, z: 18.8 }, step: 0.1 },
    aaltoOneScale: { value: 1, step: 0.1, min: 0.5, max: 20 },
  });

  return (
    <>
      <ambientLight intensity={ambientLightControls.ambientLightIntensity} color={ambientLightControls.ambientLightColor} />
      <Float rotationIntensity={5} floatIntensity={2} speed={2}>
        <spotLight position={[0, 10, 30]} angle={0.5} penumbra={1} floatingRange={[-1, 1]} intensity={2.5} color="#c7d6d4" />
      </Float>
      <mesh position={[0, 0, -30]} rotation={[0, 0, 0]}>
        <planeGeometry side={THREE.FrontSide} args={[250, 250]} />
        <meshLambertMaterial>
          <GradientTexture stops={[0, 1]} colors={["#dce3e3", "#051426"]} size={512} />
        </meshLambertMaterial>
      </mesh>
      <mesh
        scale={aaltoOneControls.aaltoOneScale}
        ref={meshRef}
        position={[aaltoOneControls.aaltoOnePosition.x, aaltoOneControls.aaltoOnePosition.y, aaltoOneControls.aaltoOnePosition.z]}
        rotation={[aaltoOneControls.aaltoOneRotation.x, aaltoOneControls.aaltoOneRotation.y, aaltoOneControls.aaltoOneRotation.z]}
        geometry={aalto.nodes.NurbsPath.geometry}
      >
        <meshPhysicalMaterial
          side={THREE.FrontSide}
          toneMapped={false}
          normalMap={normalTexture}
          normalScale={[1.75, 1.75]}
          clearcoatNormalMap={normalTexture}
          clearcoatNormalScale={[1.75, 1.75]}
          transparent={true}
          opacity={0.8}
          envMap={hdrEquirect}
          envMapIntensity={0.25}
          metalness={0}
          roughness={0}
          transmission={1}
        />
      </mesh>
      <Html fullscreen>
        <div className={`grid-container ${elementsVisible ? "" : "fade-out"}`}>
          <div className="flex-container ready-container">
            <div className="ready">
              <h1>READY</h1>
            </div>
            <div className="hr-line first-line"></div>
          </div>
          <div className="flex-container for-an-container">
            <div className="hr-line second-line"></div>
            <div className="for-an">
              <h1>FOR AN</h1>
            </div>
          </div>
          <div className="flex-container exit-container">
            <div className="exit">
              <h1>EXIT?</h1>
            </div>
            <div className="hr-line--short third-line"></div>
            <button className="dive-in-button" type="button" onClick={handleDiveInClick}>
              DIVE IN
            </button>
          </div>
        </div>
      </Html>
    </>
  );
}
